import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Search, Download, Plus, RefreshCw, Eye, Edit, Trash2, MoreVertical, AlertTriangle, X, CheckCircle } from 'lucide-react';
import MainService from '../../services/MainService';
import { normalizeVehicleGridResponse } from '../../utils/vehicleApiUtils';
import ClientListGrid, { useClientListGrid, exportClientListExcel } from '../ui/ClientListGrid';

const LIST_PAGE_SIZE = 10000;

const VEHICLE_COLUMNS = [
  { field: 'number', title: 'Vehicle Number', hideable: true },
  { field: 'model', title: 'Model', hideable: true },
  { field: 'status', title: 'Status', hideable: true },
  { field: 'actions', title: 'Actions', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const getCellValue = (row, field) => {
  switch (field) {
    case 'number':
      return row.Number || '';
    case 'model':
      return row.Model || '';
    case 'status':
      return row.IsActive ? 'Active' : 'Inactive';
    default:
      return row[field] ?? '';
  }
};

const VehicleList = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const columns = useMemo(() => VEHICLE_COLUMNS, []);

  const filteredVehicles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return vehicles;
    return vehicles.filter((vehicle) => {
      const number = String(vehicle.Number || '').toLowerCase();
      const model = String(vehicle.Model || '').toLowerCase();
      const status = vehicle.IsActive ? 'active' : 'inactive';
      return number.includes(term) || model.includes(term) || status.includes(term);
    });
  }, [vehicles, searchTerm]);

  const grid = useClientListGrid({ columns, rows: filteredVehicles, getCellValue });

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await MainService.VehiclesInGrid({
        Page: 1,
        PageSize: LIST_PAGE_SIZE,
        Keyword: '',
      });
      const { data: vehiclesList } = normalizeVehicleGridResponse(response);
      setVehicles(Array.isArray(vehiclesList) ? vehiclesList : []);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Failed to load vehicles');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleMenuOpen = (event, vehicle) => {
    setAnchorEl(event.currentTarget);
    setSelectedVehicle(vehicle);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVehicle(null);
  };

  const handleView = (vehicle) => {
    handleMenuClose();
    navigate(`/home/vehicle-management/vehicle-details/${vehicle.Id}`);
  };

  const handleEdit = (vehicle) => {
    handleMenuClose();
    navigate(`/home/vehicle-management/add-edit-vehicle/${vehicle.Id}`);
  };

  const handleDelete = (vehicle) => {
    handleMenuClose();
    setError('Delete functionality will be implemented after API is ready');
  };

  const handleAddNew = () => {
    navigate('/home/vehicle-management/add-edit-vehicle');
  };

  const exportToExcel = () => {
    try {
      exportClientListExcel({
        sheetName: 'Vehicles',
        fileName: `vehicles_${new Date().toISOString().split('T')[0]}.xlsx`,
        columns,
        groups: grid.groupField
          ? grid.displayGroups
          : [{ key: null, label: null, rows: grid.processedRows }],
        groupField: grid.groupField,
        getCellValue,
      });
      setSuccess('Vehicles exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      setError(err?.message === 'No rows to export' ? 'No data to export' : 'Failed to export vehicles');
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Truck className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Vehicle List</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Vehicle</span>
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={loadVehicles}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search vehicles by number, model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
          />
        </div>
      </div>

      <ClientListGrid
        columns={columns}
        grid={grid}
        loading={loading}
        loadingText="Loading vehicles..."
        emptyText="No vehicles found"
        minWidth="720px"
        rowKey={(row) => row.Id}
        renderCell={(row, column) => {
          if (column.field === 'status') {
            return (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                row.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {row.IsActive ? 'Active' : 'Inactive'}
              </span>
            );
          }
          if (column.field === 'actions') {
            return (
              <button
                type="button"
                onClick={(e) => handleMenuOpen(e, row)}
                title="Actions"
                className="p-2 text-[#46955A] hover:bg-[#f0f9f4] rounded-lg transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            );
          }
          return getCellValue(row, column.field) || '-';
        }}
      />

      {anchorEl && (
        <div className="fixed inset-0 z-40" onClick={handleMenuClose}></div>
      )}
      {anchorEl && (
        <div
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-1.5"
          style={{
            top: anchorEl.getBoundingClientRect().bottom + window.scrollY + 4,
            left: anchorEl.getBoundingClientRect().left + window.scrollX
          }}
        >
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleView(selectedVehicle)}
              title="View"
              aria-label="View"
              className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleEdit(selectedVehicle)}
              title="Edit"
              aria-label="Edit"
              className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(selectedVehicle)}
              title="Delete"
              aria-label="Delete"
              className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleList;
